import React, { useEffect, useMemo, useState } from "react";

import type { FxIntentNode, FxIntentType, NarrativeSonicsContext } from "./NarrativeSonics.types";

type FxLensProps = {
  context: NarrativeSonicsContext;
  nodes: FxIntentNode[];
  onUpsertNode: (node: FxIntentNode) => void;
  onRemoveNode: (nodeId: string) => void;
  compact?: boolean;
};

type FxDraft = {
  type: FxIntentType;
  startSec: number;
  endSec: number;
  strength: number;
  rationale: string;
};

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function fmt(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function makeFxIntentId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `fx-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildDefaultDraft(context: NarrativeSonicsContext): FxDraft {
  const durationSec = Math.max(1, Number(context.durationSec ?? 12));
  const startSec = clamp(Number(context.currentTimeSec ?? 0), 0, Math.max(0, durationSec - 0.75));
  const endSec = clamp(startSec + Math.min(2.5, durationSec), startSec + 0.25, durationSec);

  return {
    type: "swell",
    startSec,
    endSec,
    strength: 0.62,
    rationale: "",
  };
}

function toDraft(node: FxIntentNode): FxDraft {
  return {
    type: node.type,
    startSec: node.startSec,
    endSec: node.endSec,
    strength: node.strength,
    rationale: node.rationale ?? "",
  };
}

function typeMeta(type: FxIntentType) {
  switch (type) {
    case "swell":
      return {
        label: "SWELL",
        border: "rgba(56,189,248,0.22)",
        background: "rgba(14,165,233,0.14)",
        color: "rgba(236,254,255,0.96)",
      };
    case "duck":
      return {
        label: "DUCK",
        border: "rgba(251,191,36,0.22)",
        background: "rgba(245,158,11,0.14)",
        color: "rgba(254,243,199,0.96)",
      };
    case "release":
      return {
        label: "RELEASE",
        border: "rgba(167,139,250,0.22)",
        background: "rgba(139,92,246,0.14)",
        color: "rgba(245,243,255,0.96)",
      };
    default:
      return {
        label: "FX",
        border: "rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.06)",
        color: "rgba(255,255,255,0.84)",
      };
  }
}

function FxChip({ label, value, type }: { label: string; value: string; type: FxIntentType }): JSX.Element {
  const meta = typeMeta(type);
  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black tracking-[0.16em]"
      style={{ borderColor: meta.border, background: meta.background, color: meta.color }}
    >
      {label} {value}
    </span>
  );
}

export default function FxLens({
  context,
  nodes,
  onUpsertNode,
  onRemoveNode,
  compact = false,
}: FxLensProps): JSX.Element {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FxDraft>(() => buildDefaultDraft(context));

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  useEffect(() => {
    if (selectedNode) {
      setDraft(toDraft(selectedNode));
      return;
    }

    setDraft(buildDefaultDraft(context));
  }, [context, selectedNode]);

  useEffect(() => {
    if (!selectedNodeId) return;
    const stillExists = nodes.some((node) => node.id === selectedNodeId);
    if (!stillExists) setSelectedNodeId(null);
  }, [nodes, selectedNodeId]);

  const durationSec = Math.max(1, Number(context.durationSec ?? 12));
  const sortedNodes = useMemo(() => [...nodes].sort((a, b) => a.startSec - b.startSec), [nodes]);

  if (compact) {
    return (
      <div className="rounded-2xl border border-sky-300/16 bg-white/[0.04] p-3">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-sky-100/72">FX</div>
        <div className="mt-2 text-sm text-white/86">{sortedNodes.length} authored node{sortedNodes.length === 1 ? "" : "s"}</div>
        <div className="mt-2 text-[12px] text-white/62">Focus: {context.focusClipId || context.focusSourceId || "none"}</div>
      </div>
    );
  }

  const saveLabel = selectedNode ? "UPDATE" : "ADD";

  return (
    <div className="space-y-3 rounded-2xl border border-sky-300/16 bg-white/[0.04] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-sky-100/72">FX Intent</div>
          <div className="mt-1 text-sm text-white/88">
            Author soundtrack behavior across time without mutating canonical clips or markers.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/64">
          <span>{fmt(context.currentTimeSec)} / {fmt(context.durationSec)}</span>
          <span>Focus {context.focusClipId || context.focusSourceId || context.focusScope}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <FxChip label="TYPE" value={typeMeta(draft.type).label} type={draft.type} />
        <FxChip label="START" value={fmt(draft.startSec)} type={draft.type} />
        <FxChip label="END" value={fmt(draft.endSec)} type={draft.type} />
        <FxChip label="STR" value={draft.strength.toFixed(2)} type={draft.type} />
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3 rounded-2xl border border-white/10 bg-black/10 p-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Type</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(["swell", "duck", "release"] as FxIntentType[]).map((type) => {
                const active = draft.type === type;
                const meta = typeMeta(type);
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setDraft((prev) => ({ ...prev, type }))}
                    className="inline-flex h-8 items-center rounded-full border px-3 text-[10px] font-black tracking-[0.16em]"
                    style={{
                      borderColor: active ? meta.border : "rgba(255,255,255,0.12)",
                      background: active ? meta.background : "rgba(255,255,255,0.05)",
                      color: active ? meta.color : "rgba(255,255,255,0.82)",
                    }}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Start Sec</span>
              <input
                type="number"
                min={0}
                max={durationSec}
                step={0.1}
                value={draft.startSec}
                onChange={(event) => {
                  const startSec = clamp(Number(event.target.value || 0), 0, durationSec);
                  setDraft((prev) => ({
                    ...prev,
                    startSec,
                    endSec: Math.max(startSec + 0.1, prev.endSec),
                  }));
                }}
                className="w-full rounded-xl border border-white/12 bg-white/[0.05] px-3 py-2 text-sm text-white outline-none"
              />
            </label>

            <label className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">End Sec</span>
              <input
                type="number"
                min={0}
                max={durationSec}
                step={0.1}
                value={draft.endSec}
                onChange={(event) => {
                  const endSec = clamp(Number(event.target.value || 0), 0, durationSec);
                  setDraft((prev) => ({
                    ...prev,
                    endSec: Math.max(prev.startSec + 0.1, endSec),
                  }));
                }}
                className="w-full rounded-xl border border-white/12 bg-white/[0.05] px-3 py-2 text-sm text-white outline-none"
              />
            </label>
          </div>

          <label className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Strength</span>
              <span className="text-[11px] text-white/70">{draft.strength.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={draft.strength}
              onChange={(event) => setDraft((prev) => ({ ...prev, strength: Number(event.target.value) }))}
              className="w-full accent-sky-300"
            />
          </label>

          <label className="space-y-1">
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Rationale</span>
            <input
              type="text"
              value={draft.rationale}
              onChange={(event) => setDraft((prev) => ({ ...prev, rationale: event.target.value }))}
              placeholder="Optional editorial note"
              className="w-full rounded-xl border border-white/12 bg-white/[0.05] px-3 py-2 text-sm text-white outline-none placeholder:text-white/38"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                const startSec = clamp(Math.min(draft.startSec, draft.endSec - 0.1), 0, durationSec);
                const endSec = clamp(Math.max(draft.endSec, startSec + 0.1), startSec + 0.1, durationSec);

                onUpsertNode({
                  id: selectedNode?.id ?? makeFxIntentId(),
                  type: draft.type,
                  startSec,
                  endSec,
                  strength: clamp(draft.strength, 0, 1),
                  rationale: draft.rationale.trim() || undefined,
                  authoredBy: "authored",
                  focusScope: context.focusScope,
                  focusClipId: context.focusClipId,
                  focusSourceId: context.focusSourceId,
                });
              }}
              className="inline-flex h-9 items-center rounded-full border border-sky-300/24 bg-sky-400/14 px-4 text-[10px] font-black tracking-[0.18em] text-sky-50"
            >
              {saveLabel}
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedNodeId(null);
                setDraft(buildDefaultDraft(context));
              }}
              className="inline-flex h-9 items-center rounded-full border border-white/12 bg-white/[0.06] px-4 text-[10px] font-black tracking-[0.18em] text-white/78"
            >
              RESET
            </button>

            {selectedNode ? (
              <button
                type="button"
                onClick={() => {
                  onRemoveNode(selectedNode.id);
                  setSelectedNodeId(null);
                }}
                className="inline-flex h-9 items-center rounded-full border border-rose-300/24 bg-rose-400/12 px-4 text-[10px] font-black tracking-[0.18em] text-rose-50"
              >
                REMOVE
              </button>
            ) : null}
          </div>
        </div>

        <div className="space-y-2 rounded-2xl border border-white/10 bg-black/10 p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Authored FX Nodes</div>
          {sortedNodes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.03] px-3 py-4 text-[12px] text-white/60">
              No FX intent authored yet. Add a swell, duck, or release region to land soundtrack behavior over time.
            </div>
          ) : (
            sortedNodes.map((node) => {
              const active = node.id === selectedNodeId;
              const meta = typeMeta(node.type);
              return (
                <button
                  key={node.id}
                  type="button"
                  onClick={() => setSelectedNodeId(node.id)}
                  className="w-full rounded-xl border px-3 py-3 text-left transition-colors"
                  style={{
                    borderColor: active ? meta.border : "rgba(255,255,255,0.10)",
                    background: active ? meta.background : "rgba(255,255,255,0.04)",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[12px] font-semibold text-white/92">
                      {fmt(node.startSec)} to {fmt(node.endSec)}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: meta.color }}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <FxChip label="STR" value={node.strength.toFixed(2)} type={node.type} />
                    <FxChip label="FOCUS" value={(node.focusScope || context.focusScope).toUpperCase()} type={node.type} />
                  </div>
                  {node.rationale ? <div className="mt-2 text-[12px] text-white/68">{node.rationale}</div> : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}